import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, EmptyState, ErrorState, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { OverviewTiles, type Tile } from '@/components/overview-tiles';
import { useErrorMessage } from '@/lib/mutation';
import { debtorsQuery, expenseCategoriesQuery, expenseReportQuery, revenueQuery } from '@/features/billing/queries';

export const Route = createFileRoute('/_app/billing/reports')({ component: BillingReports });

const BUCKETS = ['notDue', 'days1to30', 'days31to60', 'days61to90', 'over90'] as const;

/** The fixed reports of billing (CO-0367): debtors by age, revenue per month with the unbilled total, expenses per category. */
function BillingReports() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t('billingReports.title')} description={t('billingReports.description')} />
      <div className="grid gap-6">
        <Debtors />
        <Revenue />
        <ExpenseReport />
      </div>
    </>
  );
}

function Debtors() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery(debtorsQuery({}));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-40" />;
  const d = q.data;
  const percent = (share: number | null) => (share === null ? '–' : `${formatNumber(share * 100)}%`);
  const days = (n: number | null) => (n === null ? '–' : t('billingReports.days', { days: formatNumber(n, 'nl', 1) }));
  const amount = d.sentFromApp.amounts[0];
  const tiles: Tile[] = [
    {
      label: t('billingReports.sentFromApp', { from: formatDate(d.sentFromApp.from) }),
      value: t('billingReports.sentFromAppValue', { count: d.sentFromApp.count, total: d.sentFromApp.sentCount, share: percent(d.sentFromApp.share) }),
      detail: amount ? t('billingReports.sentFromAppAmount', { amount: formatMoney(amount.totalCents, amount.currency), total: formatMoney(amount.sentTotalCents, amount.currency) }) : undefined,
      tone: d.sentFromApp.sentCount > 0 && d.sentFromApp.count === 0 ? 'attention' : undefined,
    },
    {
      label: t('billingReports.dso'),
      value: days(d.dso.days),
      detail: t('billingReports.dsoDetail', { paid: d.dso.paidInvoices, age: days(d.dso.openAgeDays) }),
    },
  ];
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-lg font-bold">{t('billingReports.debtors')}</h2>
      <OverviewTiles label={t('billingReports.debtorFigures')} tiles={tiles} />
      {!d.items.length ? (
        <EmptyState title={t('billingReports.noDebtors')} />
      ) : (
        <Table data-testid="debtors">
          <THead>
            <Tr>
              <Th>{t('invoices.customer')}</Th>
              {BUCKETS.map((b) => (
                <Th key={b} className="text-right">
                  {t(`billingReports.bucket.${b}`)}
                </Th>
              ))}
              <Th className="text-right">{t('invoices.fields.open')}</Th>
            </Tr>
          </THead>
          <TBody>
            {d.items.map((r) => (
              <Tr key={`${r.organisationId}:${r.currency}`}>
                <Td>
                  <Link to="/billing/invoices" search={{ organisationId: r.organisationId, overdue: true }} className="hover:underline">
                    {r.organisationName}
                  </Link>
                  {r.oldestDueDate && <div className="text-xs text-fg-3">{t('billingReports.oldest', { date: formatDate(r.oldestDueDate) })}</div>}
                </Td>
                {BUCKETS.map((b) => (
                  <Td key={b} className={`text-right tabular-nums ${b === 'over90' && r.buckets[b] ? 'font-bold text-danger' : ''}`}>
                    {r.buckets[b] ? formatMoney(r.buckets[b], r.currency) : '–'}
                  </Td>
                ))}
                <Td className="text-right font-bold tabular-nums">{formatMoney(r.openCents, r.currency)}</Td>
              </Tr>
            ))}
            <Tr>
              <Td className="font-bold">{t('invoices.fields.total')}</Td>
              {BUCKETS.map((b) => (
                <Td key={b} className="text-right font-bold tabular-nums">
                  {formatMoney(d.totals.buckets[b])}
                </Td>
              ))}
              <Td className="text-right font-bold tabular-nums">{formatMoney(d.totals.openCents)}</Td>
            </Tr>
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function Revenue() {
  const { t, i18n } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery(revenueQuery({}));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-40" />;
  const d = q.data;
  const label = (m: string) => new Intl.DateTimeFormat(i18n.language, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${m}-15T12:00:00Z`));
  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">{t('billingReports.revenue')}</h2>
        <span className="text-sm text-fg-2" data-testid="unbilled">
          {t('billingReports.unbilled')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(d.unbilled.totalCents)}</span>
          <span className="text-fg-3">
            {' '}
            ({t('billingReports.unbilledParts', { time: formatMoney(d.unbilled.timeCents), mileage: formatMoney(d.unbilled.mileageCents), expenses: formatMoney(d.unbilled.expenseCents) })})
          </span>
        </span>
      </div>
      <Table data-testid="revenue">
        <THead>
          <Tr>
            <Th>{t('billingReports.month')}</Th>
            <Th className="text-right">{t('billingReports.revenueExVat')}</Th>
            <Th className="hidden text-right sm:table-cell">{t('invoices.fields.vat')}</Th>
            <Th className="hidden text-right md:table-cell">{t('billingReports.invoiceCount')}</Th>
            <Th className="text-right">{t('billingReports.received')}</Th>
          </Tr>
        </THead>
        <TBody>
          {d.months.map((m) => (
            <Tr key={m.month}>
              <Td className="capitalize">{label(m.month)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(m.revenueCents)}</Td>
              <Td className="hidden text-right tabular-nums sm:table-cell">{formatMoney(m.vatCents)}</Td>
              <Td className="hidden text-right tabular-nums md:table-cell">
                {m.invoices}
                {m.creditNotes ? ` / ${m.creditNotes}` : ''}
              </Td>
              <Td className="text-right tabular-nums">{formatMoney(m.paidCents)}</Td>
            </Tr>
          ))}
          <Tr>
            <Td className="font-bold">{t('invoices.fields.total')}</Td>
            <Td className="text-right font-bold tabular-nums">{formatMoney(d.totals.revenueCents)}</Td>
            <Td className="hidden text-right font-bold tabular-nums sm:table-cell">{formatMoney(d.totals.vatCents)}</Td>
            <Td className="hidden md:table-cell" />
            <Td className="text-right font-bold tabular-nums">{formatMoney(d.totals.paidCents)}</Td>
          </Tr>
        </TBody>
      </Table>
    </Card>
  );
}

function ExpenseReport() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery(expenseReportQuery({}));
  const categories = useQuery(expenseCategoriesQuery);
  const labelOf = (v: string) => categories.data?.items.find((c) => c.value === v)?.label ?? v;
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-40" />;
  const d = q.data;
  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">{t('billingReports.expenses')}</h2>
        <span className="text-sm text-fg-3">{d.everyone ? t('billingReports.everyone') : t('billingReports.own')}</span>
      </div>
      {!d.items.length ? (
        <EmptyState title={t('billingReports.noExpenses')} />
      ) : (
        <Table data-testid="expense-report">
          <THead>
            <Tr>
              <Th>{t('expenses.fields.category')}</Th>
              <Th className="text-right">{t('expenses.withoutVat')}</Th>
            </Tr>
          </THead>
          <TBody>
            {d.categories.map((c) => (
              <Tr key={c.category}>
                <Td>{labelOf(c.category)}</Td>
                <Td className="text-right tabular-nums">{formatMoney(c.amountCents)}</Td>
              </Tr>
            ))}
            <Tr>
              <Td className="font-bold">{t('invoices.fields.total')}</Td>
              <Td className="text-right font-bold tabular-nums">{formatMoney(d.totals.amountCents)}</Td>
            </Tr>
          </TBody>
        </Table>
      )}
      <p className="text-sm text-fg-2">
        {t('billingReports.expenseTotals', { vat: formatMoney(d.totals.vatCents), billable: formatMoney(d.totals.billableAmountCents) })}
      </p>
    </Card>
  );
}
